/**
 * Pure helpers behind the reframe modal's Speakers tab (PLAT-11992): which
 * faces a scene shows, who each belongs to, and what assigning one changes.
 */

import { MIN_OTHER_CLUSTER_SHARE } from './otherSpeakerCluster';
import {
  buildSpeakerLabelToFaceLabelsMap,
  type SpeakerLabelToIdMappings,
} from './speakerRanking';

import type {
  FaceDetection,
  FaceTracks,
  SpeakerIdentifications,
} from './types';

/**
 * Face labels on screen for a real share of the scene, most present first.
 * Brief detections (passers-by, a face caught mid-cut) drop out.
 */
export function listSceneFaces(
  sceneDetections: ReadonlyArray<Pick<FaceDetection, 'label'>>,
): Array<{ faceLabel: string; count: number }> {
  const counts = new Map<string, number>();
  for (const { label } of sceneDetections) {
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  return [...counts.entries()]
    .filter(
      ([, count]) => count / sceneDetections.length >= MIN_OTHER_CLUSTER_SHARE,
    )
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([faceLabel, count]) => ({ faceLabel, count }));
}

/** The transcript speaker whose faces include `faceLabel`, or null. */
export function speakerForFace(
  speakerMap: Readonly<Record<string, readonly string[]>> | null,
  faceLabel: string,
): string | null {
  for (const [speakerLabel, faceLabels] of Object.entries(speakerMap ?? {})) {
    if (faceLabels.includes(faceLabel)) return speakerLabel;
  }
  return null;
}

/**
 * The story's face identifications with `faceLabel` given to `datoSpeakerId`
 * (or removed, for null), and the transcript speaker → faces map they make.
 */
export function assignFace({
  identifications,
  voiceMappings,
  faceTracks,
  faceLabel,
  datoSpeakerId,
}: {
  identifications: SpeakerIdentifications;
  voiceMappings: SpeakerLabelToIdMappings;
  faceTracks: FaceTracks;
  faceLabel: string;
  datoSpeakerId: string | null;
}): {
  identifications: SpeakerIdentifications;
  speakerMap: Record<string, string[]>;
} {
  const next = { ...identifications };
  if (datoSpeakerId) {
    next[faceLabel] = datoSpeakerId;
  } else {
    delete next[faceLabel];
  }
  return {
    identifications: next,
    speakerMap: buildSpeakerLabelToFaceLabelsMap(
      voiceMappings,
      next,
      faceTracks,
    ),
  };
}

/** A label must hold at least this share of the in-crop detections to count as framed — not a shoulder clipped at the edge. */
export const MIN_LABEL_SHARE_IN_CROP = 0.3;

/** Normalised 0–1 rectangle in source-frame space, like `FaceDetection.box`. */
export type NormalizedRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/** Face labels whose box centre falls inside `crop`, most detections first. */
export function faceLabelsInCrop(
  detections: ReadonlyArray<Pick<FaceDetection, 'label' | 'box'>>,
  crop: NormalizedRect,
): string[] {
  const counts = new Map<string, number>();
  let inCrop = 0;
  for (const { label, box } of detections) {
    const centerX = box[0] + box[2] / 2;
    const centerY = box[1] + box[3] / 2;
    if (
      centerX < crop.x ||
      centerX > crop.x + crop.width ||
      centerY < crop.y ||
      centerY > crop.y + crop.height
    ) {
      continue;
    }
    inCrop++;
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  return [...counts.entries()]
    .filter(([, count]) => count / inCrop >= MIN_LABEL_SHARE_IN_CROP)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([label]) => label);
}
